import type { BasketUpsellProduct, BasketUpsellResponse } from '@library/api-menu-experience'

import type { LimitReached } from './basketUpsellLimits'

type BasketProductQuantities = Record<string, number>

export type BasketUpsellProductActionSource =
  | 'basket'
  | 'basket_rth_carousel'
  | 'product_detail_modal'

type ProductAction = (productId: string, source: BasketUpsellProductActionSource) => void
type GetLimitReached = (productId: string) => LimitReached

type BuildBasketUpsellInput = {
  basketProductQuantities: BasketProductQuantities
  getLimitReached: GetLimitReached
  onAddProduct: ProductAction
  onClickProduct: (product: { id: string }) => void
  onRemoveProduct: ProductAction
  shouldShowBasketUpsells: boolean
  upsellResponse: BasketUpsellResponse | null
}

type BuildProductDetailInput = {
  basketProductQuantities: BasketProductQuantities
  getLimitReached: GetLimitReached
  onAddProduct: ProductAction
  onClose: () => void
  onRemoveProduct: ProductAction
  productDetailsId: string | null
  products: Record<string, BasketUpsellProduct>
}

const getVisibleCarouselProducts = (upsellResponse: BasketUpsellResponse): BasketUpsellProduct[] =>
  upsellResponse.carousel?.productIds
    .map((productId) => upsellResponse.products[productId])
    .filter((product): product is BasketUpsellProduct => Boolean(product) && product.stock > 0) ??
  []

const getSelectedUpsellProducts = (
  basketProductQuantities: BasketProductQuantities,
  upsellProductsById: Record<string, BasketUpsellProduct>,
): BasketUpsellProduct[] =>
  Object.keys(basketProductQuantities)
    .map((productId) => upsellProductsById[productId])
    .filter((product): product is BasketUpsellProduct => Boolean(product))

export const buildBasketUpsellViewModel = ({
  basketProductQuantities,
  getLimitReached,
  onAddProduct,
  onClickProduct,
  onRemoveProduct,
  shouldShowBasketUpsells,
  upsellResponse,
}: BuildBasketUpsellInput) => {
  if (!shouldShowBasketUpsells || !upsellResponse?.carousel) return undefined

  const toProductView = (product: BasketUpsellProduct) => {
    const qty = basketProductQuantities[product.id] ?? 0
    const limitReached = getLimitReached(product.id)
    const outOfStock = product.stock - qty <= 0

    return {
      ...product,
      isAvailable: !outOfStock && !limitReached,
      limitReached,
      outOfStock,
      qty,
    }
  }

  return {
    carousel: {
      tag: upsellResponse.carousel.tag,
      title: upsellResponse.carousel.title,
      subtitle: upsellResponse.carousel.subtitle,
    },
    products: getVisibleCarouselProducts(upsellResponse).map(toProductView),
    selectedProducts: getSelectedUpsellProducts(
      basketProductQuantities,
      upsellResponse.products,
    ).map(toProductView),
    onAddProduct,
    onClickProduct,
    onRemoveProduct,
  }
}

export const buildProductDetailViewModel = ({
  basketProductQuantities,
  getLimitReached,
  onAddProduct,
  onClose,
  onRemoveProduct,
  productDetailsId,
  products,
}: BuildProductDetailInput) => {
  const product = productDetailsId ? products[productDetailsId] : null

  if (!product) return null

  const qty = basketProductQuantities[product.id] ?? 0
  const limitReached = getLimitReached(product.id)
  const hasNoStockAvailable = product.stock - qty <= 0
  const outOfStock = product.stock <= 0

  return {
    isAvailable: !hasNoStockAvailable && !limitReached,
    limitReached,
    onAdd: (productId: string) => onAddProduct(productId, 'product_detail_modal'),
    onClose,
    onRemove: (productId: string) => onRemoveProduct(productId, 'product_detail_modal'),
    outOfStock,
    product,
    qty,
  }
}
